import {
  dehydrate,
  hydrate,
  QueryClient,
  type DehydratedState,
  type QueryKey,
} from '@tanstack/react-query'
import { DASHBOARD_QUERY_ROOT } from '../inventory/soldDateQueryBounds'

export const DASHBOARD_CACHE_STORAGE_KEY = 'otw-dashboard-query-cache-v1'
export const DASHBOARD_CACHE_MAX_BYTES = 3_500_000

const DASHBOARD_WIDGET_ROOT = 'dashboard-widget'
const DASHBOARD_ROLLUP_ROOT = 'dashboard-rollup'
const EXPENSES_ROOT = 'expenses'

/** Full expense lists are too large for sessionStorage and hydrate + refetch OOM'd Chrome tabs. */
export const isPersistableDashboardQueryKey = (queryKey: QueryKey): boolean => {
  const root = queryKey[0]
  if (root === EXPENSES_ROOT) return false
  return (
    root === DASHBOARD_QUERY_ROOT ||
    root === DASHBOARD_WIDGET_ROOT ||
    root === DASHBOARD_ROLLUP_ROOT ||
    root === 'action-banner' // ACTION_BANNER_QUERY_ROOT — keep in sync with actionBannerQueries.ts
  )
}

const stripHeavyQueriesFromDehydrated = (dehydrated: DehydratedState): DehydratedState => {
  const queries = dehydrated.queries?.filter(entry => entry.queryKey[0] !== EXPENSES_ROOT)
  if (!queries || queries.length === (dehydrated.queries?.length ?? 0)) {
    return dehydrated
  }
  return { ...dehydrated, queries }
}

/**
 * Restore persisted Overview queries. Keep hydrated `dataUpdatedAt` so React Query
 * treats the data as present (no `isLoading` skeleton flash). Soft-invalidate after
 * paint so a background revalidate can run via `isFetching` only.
 */
export const restoreDashboardQueryCache = (client: QueryClient, storage: Storage | undefined): boolean => {
  if (!storage) return false
  try {
    const raw = storage.getItem(DASHBOARD_CACHE_STORAGE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as DehydratedState
    hydrate(client, stripHeavyQueriesFromDehydrated(parsed))
    return true
  } catch {
    try {
      storage.removeItem(DASHBOARD_CACHE_STORAGE_KEY)
    } catch {
      /* ignore */
    }
    return false
  }
}

export const persistDashboardQueryCache = (client: QueryClient, storage: Storage | undefined): boolean => {
  if (!storage) return false
  try {
    const dehydrated = dehydrate(client, {
      shouldDehydrateQuery: query =>
        query.state.status === 'success' &&
        query.state.data !== undefined &&
        query.state.data !== null &&
        isPersistableDashboardQueryKey(query.queryKey),
    })
    const serialized = JSON.stringify(dehydrated)
    if (serialized.length > DASHBOARD_CACHE_MAX_BYTES) return false
    storage.setItem(DASHBOARD_CACHE_STORAGE_KEY, serialized)
    return true
  } catch {
    return false
  }
}

export const subscribeDashboardQueryPersist = (
  client: QueryClient,
  storage: Storage | undefined,
): (() => void) => {
  if (!storage) return () => undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  const persistNow = () => {
    persistDashboardQueryCache(client, storage)
  }
  const schedule = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(persistNow, 250)
  }
  const unsubscribe = client.getQueryCache().subscribe(schedule)
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', persistNow)
  }
  return () => {
    unsubscribe()
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', persistNow)
    }
    if (timer) clearTimeout(timer)
  }
}

export const createPersistedDashboardQueryClient = (): QueryClient => {
  const client = new QueryClient()
  const storage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  const restored = restoreDashboardQueryCache(client, storage)
  if (typeof window !== 'undefined') {
    ;(window as Window & { __OTW_DASHBOARD_CACHE_RESTORED__?: boolean }).__OTW_DASHBOARD_CACHE_RESTORED__ =
      restored
  }
  subscribeDashboardQueryPersist(client, storage)
  return client
}
